import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { PHPermissions } from '../../models/PHPermissions';
import { ProductCard } from '../../../product/models/product-card';
import { ProductsService } from '../../services/products.service';
import { lastValueFrom, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { ProductCardService } from '../../services/product-card.service';
import { ITableData } from '../../models/table';
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { Utils } from '../../services/utils.service';
import { SearchAlgorithmType } from '../../models/personalSettings';
import { ExcelService } from '../../services/xls.service';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
	ISearchSettings,
	IUpdateProductPosition,
	MAX_MP_PRIORITY_VALUE,
	MPProductsPayload,
	ProductCardTab
} from '../../../product/models/models';
import { IMasterProductsTableItem } from '../../../product/models/masterProductsTableItem';
import { IAddProductsTableItem } from '../../models/addProductsTableItem';
import { PhSimpleTableComponent } from '../ph-simple-table/ph-simple-table.component';
import {
	IAddProductsPanelFilters,
	IAddProductsPanelParams
} from '../../../planogram/planogram-edit/models/addProductsPanelFilters';
import { RequestParams } from '../../models/requestParams';
import { TableService } from '../../services/table.service';
import { Filter, FilterID, FilterItem, setDefaults } from '../filters-panel/models/filter';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { CategoriesParams, FiltersService, MarkersParams } from '../filters-panel/filters.service';
import { FiltersPanelService } from '../filters-panel/filters-panel.service';
import { AddProductsService } from '../../services/add-products.service';
import { SortingListData } from '../ph-list/ph-list.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { ProductsAddExcelModalComponent } from '../../../planogram/planogram-edit/modals/products-add-excel-modal/products-add-excel-modal.component';
import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { SearchComponent } from '../search/search.component';
import { LoaderComponent } from '../loader/loader.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DownloadService } from '../../services/download.service';

const BASE_SEARCH_FIELDS: Array<ISearchSettings> = [
	{ title: 'PRODUCT_CARD.SEARCH_BY_ARTICLE', selected: false, name: 'article' },
	{ title: 'PRODUCT_CARD.SEARCH_BY_BARCODE', selected: false, name: 'barcode' },
	{ title: 'PRODUCT_CARD.SEARCH_BY_NAME', selected: false, name: 'name' }
];

@Component({
    selector: 'app-master-products-tables[product]',
    templateUrl: './master-products-tables.component.html',
    styleUrls: ['./master-products-tables.component.scss'],
	imports: [
		SHARED_MODULES,
		FaIconComponent,
		LoaderComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhSimpleTableComponent,
		SearchComponent
	],
    standalone: true
})
export class MasterProductsTablesComponent implements OnInit, OnDestroy {

	private _filters: Array<Filter> = [];
	private _filtersApplySubscription: Subscription;
	private _loadAddProductsData$: ReplaySubject<void> = new ReplaySubject<void>(1);
	private _isLoadedAddProductsNext: boolean = false;
	private _requestParams: RequestParams;
	private _selectedTablePage: number = 1;
	private _subscriptions: Array<Subscription> = [];

	public addTableData: ITableData<IMasterProductsTableItem>;
	public addTableLoading: boolean = false;
	public editAccess: boolean = false;
	public filtersApplied: boolean = false;
	public isDirty: Map<ProductCardTab, boolean>;
    public isExporting: WritableSignal<boolean> = signal<boolean>(false);
	public maxProductCountInMP: number;
	public needUpdateAddProducts: boolean = false;
	public productCardTab: typeof ProductCardTab = ProductCardTab;
	public searchQuery: string;
	public searchSettings: { selectedProducts: Array<ISearchSettings>, addProducts: Array<ISearchSettings> };
	public showAddLoader: boolean = false;
	public showAddAllProductsLoader: boolean = false;
	public showLoader: boolean = false;
	public selectedTableData: ITableData<IMasterProductsTableItem>;
	public sortingAddProductsSubject: Subject<string> = new Subject<string>();
	public sortingSelectedProductsSubject: Subject<string> = new Subject<string>();
	public sortingSelectedProductsValue: SortingListData;
	public updateAddProductsSubject: Subject<void> = new Subject<void>();
	public updateSelectedProductsSubject: Subject<void> = new Subject<void>();

	@Input('product') product: ProductCard;
	@Input('storeId') storeId: number;

	@Output('cancelPanel') cancelEmitter: EventEmitter<void> = new EventEmitter<void>();

	@ViewChild('addPanel') addExpansionPanel: MatExpansionPanel;
	@ViewChild('addProductsTable') addProductsTableComponent: PhSimpleTableComponent;
	@ViewChild('selectedProductsTable') selectedProductsTableComponent: PhSimpleTableComponent;

	constructor(
        private dialog: MatDialog,
		private translate: TranslateService,
		private addProductsService: AddProductsService,
		private productsService: ProductsService,
		private productCardService: ProductCardService,
		private sessionService: SessionService,
		private snackBarService: SnackBarService,
		private excelService: ExcelService,
		private tableService: TableService,
		private filtersPanelService: FiltersPanelService,
		private filtersService: FiltersService,
        private downloadService: DownloadService
	) {
		this.editAccess = this.sessionService.hasAccess(PHPermissions.ProductParametersEdit);
		this.isDirty = this.productCardService.isDirty;
		this.maxProductCountInMP = this.sessionService.defaults.max_products_in_mp;
		this.searchSettings = {
			selectedProducts: BASE_SEARCH_FIELDS.map((setting: ISearchSettings) => ({ ...setting })),
			addProducts: BASE_SEARCH_FIELDS.map((setting: ISearchSettings) => ({ ...setting }))
		};
	}

	set editedTableData(data: Array<IMasterProductsTableItem>) {
		this.product.masterProducts.data.results.dataSource = data;
	}

	get editedTableData(): Array<IMasterProductsTableItem> {
		return this.product.masterProducts.data.results.dataSource;
	}

	get filteredTableData(): Array<IMasterProductsTableItem> {
		const searchAlgorithm: SearchAlgorithmType = this.sessionService.personalSettings.searchAlgorithm;
		const allFieldsUnselected: boolean = this.searchSettings.selectedProducts
			.every((setting: ISearchSettings) => !setting.selected);
		const searchFields: Array<string> = this.searchSettings.selectedProducts
			.filter((setting: ISearchSettings) => allFieldsUnselected || setting.selected)
			.map((setting: ISearchSettings) => setting.name);

		return this.editedTableData
			.filter((item: IMasterProductsTableItem) =>
				searchFields.some((field: string) =>
					field === 'name'
						? Utils.searchItem(item.product_name, ['value'], this.searchQuery, searchAlgorithm)
						: Utils.searchItem(item, [field], this.searchQuery, searchAlgorithm)
				)
			)
			.sort((a: IMasterProductsTableItem, b: IMasterProductsTableItem) => {
				if (this.sortingSelectedProductsValue?.value && this.sortingSelectedProductsValue.active === 'position') {
					const posA: number = a.position?.position;
					const posB: number = b.position?.position;

					if (posA == null && posB == null) return 0;
					if (posA == null) return 1;
					if (posB == null) return -1;

					return this.sortingSelectedProductsValue.direction === 'asc' ? posA - posB : posB - posA;
				}
				return 0;
			});
	}

	ngOnInit(): void {
		this._subscriptions.push(
			this.productCardService.closeSubject.subscribe(() => {
				this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
			}),

			this.productCardService.updatePositionSubject.subscribe((data: IUpdateProductPosition) => {
				const productItem: IMasterProductsTableItem = this.editedTableData
					.find((item: IMasterProductsTableItem) => item.key === data.id);
				productItem.position.position = data.position;
				this.updateProductPositions(productItem);
				this.isDirty.set(ProductCardTab.MasterProducts, true);
			}),

			this._loadAddProductsData$
				.pipe(
					switchMap(() => {
						const params: Partial<IAddProductsPanelParams> = this._requestParams.getParams();

						if (this.editedTableData.length) {
							Utils.assignObject(params, {
								exclude_ids: this.editedTableData.map((item: IMasterProductsTableItem) => item.clickable_params.product_id).join(',')
							});
						}

						return this.productsService
							.getProductsTable(params)
							.pipe(
								finalize(() => {
									this.showAddLoader = false;
									this.addTableLoading = false;
								}),
								catchError(() => {
									this.snackBarService.setErrorNotification();
									return of({
										has_data: false,
										next: null,
										count: 0,
										results: {
											columns: [],
											dataSource: []
										}
									});
								})
							);
					})
				)
				.subscribe((response: ITableData<IMasterProductsTableItem>) => {
					this.addTableData = response;
					this.needUpdateAddProducts = false;
				}),

			this.updateAddProductsSubject
				.pipe(
					switchMap(() => {
						this._isLoadedAddProductsNext = true;
						return this.tableService.getNextTablePage<ITableData<IAddProductsTableItem>, IAddProductsPanelFilters>(
							'GET',
							this.addTableData.next
						);
					}),
					catchError(() => {
						this.snackBarService.setErrorNotification();
						return of({
							has_data: false,
							next: null,
							count: 0,
							results: {
								columns: [],
								dataSource: []
							}
						});
					})
				)
				.subscribe((response: ITableData<IMasterProductsTableItem>) => {
					const data: Array<IMasterProductsTableItem> = this.addTableData.results.dataSource;
					data.push(...response.results.dataSource);
					this.addTableData = response;
					this.addTableData.results.dataSource = data;
					this._isLoadedAddProductsNext = false;
				}),

			this.updateSelectedProductsSubject.subscribe(() => {
					this._selectedTablePage++;
					this.initSelectedProducts();
			}),

			this.sortingAddProductsSubject.subscribe((value: string) => {
				this._requestParams.updateParams({ ordering: value !== 'default' ? value : '' });
				this.loadAddProductsData();
			}),

			this.sortingSelectedProductsSubject.subscribe((value: string) => {
				const sortingValue: string = value !== 'default' ? value : null;
				this.sortingSelectedProductsValue = {
					value: sortingValue,
					active: value.replace('-', ''),
					direction: value.startsWith('-') ? 'desc' : (value !== 'default' ? 'asc' : '')
				}
				this._selectedTablePage = 1;
				this.initSelectedProducts();
			}),

			this.addProductsService.addProductsSubject.subscribe((id: number) => {
				if (this.editedTableData.length >= this.sessionService.defaults.max_products_in_mp) {
					this.snackBarService.setSnackBarNotification({
						messageText: 'MASTER_POSITION.ADD_TOO_MANY_PRODUCTS'
					});
					this.addProductsService.addedEndSubject.next(id);
					return;
				}

				const tableItem: IMasterProductsTableItem = this.addTableData.results.dataSource
					.find((item: IMasterProductsTableItem) => item.key === id);
				tableItem.position = { position: null, dtype: 'mp_product_position' };
				this.product.masterProducts.data.results.dataSource.unshift(tableItem);
				this.product.masterProducts.data.count++;
				this.product.masterProducts.data.has_data = true;
				this.addTableData.count--;
				this.addTableData.has_data = !!this.addTableData.count || this.filtersApplied || this._requestParams.getParam('search');
				this.addTableData.results.dataSource = this.addTableData.results.dataSource
					.filter((item: IMasterProductsTableItem) => item.key !== id);
				this.addTableData = {...this.addTableData};
				if (this.addTableData.results.dataSource.length < 20 && this.addTableData.next && !this._isLoadedAddProductsNext) {
					this.updateAddProductsSubject.next();
				}
				this.sortingSelectedProductsValue = {
					direction: '',
					active: '',
					value: ''
				}
				this.initSelectedProducts();
				this.isDirty.set(ProductCardTab.MasterProducts, true);
			})
		);

		if (this.product.loadedMasterProducts) {
			this.initSelectedProducts();
			return;
		}

		this.loadData();
	}

	private initSelectedProducts(): void {
		this.selectedTableData = {
			...this.product.masterProducts.data,
			count: this.filteredTableData.length,
			next: this.filteredTableData.length > (this._selectedTablePage * 30) ? 'yes' : null,
			results: {
				columns: this.product.masterProducts.data.results.columns,
				dataSource: this.filteredTableData.slice(0, this._selectedTablePage * 30)
			}
		};
	}

	private updateProductPositions(item: IMasterProductsTableItem): void {
		let seenValues: Set<number> = new Set<number>();
		seenValues.add(item.position.position);
		for (let i = 0; i < this.editedTableData.length; i++) {
			if (this.editedTableData[i] === item || Utils.isNullOrUndefined(this.editedTableData[i].position.position)) {
				continue;
			}
			let row: IMasterProductsTableItem = this.editedTableData[i];
			while (seenValues.has(row.position.position)) {
				row.position.position++;
				if (row.position.position > MAX_MP_PRIORITY_VALUE) row.position.position = null;
			}

			seenValues.add(row.position.position);
		}

		this.initSelectedProducts();
	}

	public addAllProducts(): void {
		this.showAddAllProductsLoader = true;
		if (this.addProductsTableComponent) this.addProductsTableComponent.startLoading();
		const params: Partial<IAddProductsPanelParams> = this._requestParams.getParams(null, ['page', 'page_size']);
		if (this.editedTableData.length) {
			Utils.assignObject(params, {
				exclude_ids: this.editedTableData.map((item: IMasterProductsTableItem) => item.clickable_params.product_id).join(',')
			});
		}

		this.productsService.getProductsTable(params).subscribe({
			next: (response: ITableData<IMasterProductsTableItem>) => {
				const data: Array<IMasterProductsTableItem> = response.results.dataSource
					.map((item: IMasterProductsTableItem) => ({
						...item,
						position: { dtype: 'mp_product_position', position: null }
					}));
				this.product.masterProducts.data.results.dataSource.unshift(...data);
				this.product.masterProducts.data.count += data.length;
				this.product.masterProducts.data.has_data = true;
				this.loadAddProductsData();
				this.sortingSelectedProductsValue = {
					direction: '',
					active: '',
					value: ''
				}
				this.initSelectedProducts();
				this.isDirty.set(ProductCardTab.MasterProducts, true);
				this.showAddAllProductsLoader = false;
			},
			error: () => {
				this.showAddAllProductsLoader = false;
				this.snackBarService.setErrorNotification();
			}
		});
	}

	public clearFilters(): void {
		if (this._filtersApplySubscription) {
			this.filtersPanelService.setApplyPanel(false);
		} else {
			const searchQuery: string = this._requestParams.getParam('search'),
				sorting: string = this._requestParams.getParam('ordering');
			this._requestParams.clearParams();
			this._requestParams.updateParams({
				page_size: 30,
				search: searchQuery,
				ordering: sorting,
				shops: this.storeId === null ? -1 : this.storeId ?? ''
			});
			this.filtersApplied = false;
			this.loadAddProductsData();
		}
	}

	public export(): void {
        this.isExporting.set(true);
		const name: string = `${this.translate.instant('MASTER_POSITION.TITLE')}_${this.product.name}`;

        const request$: Observable<void> = this.excelService
            .getMPProducts(this.product.id, name)
            .pipe(finalize(() => this.isExporting.set(false)));

        this.downloadService.excelFilesDownloadSubject.next({
            name: name,
            getData$: request$
        });
	}

	public loadAddProductsData(): void {
		this.addTableLoading = true;
		if (this.addProductsTableComponent) {
			this.addProductsTableComponent.scrollToTop();
			this.addProductsTableComponent.startLoading();
		}
		this._loadAddProductsData$.next();
	}

	public loadData(): void {
		this.showLoader = true;
		this.selectedTableData = null;
		this.product.masterProducts = null;
		const params: any = {
			page: 1,
			page_size: this.sessionService.defaults.max_products_in_mp
		};
		if (this.storeId !== undefined) {
			params.shops = this.storeId === null ? -1 : this.storeId;
		}
		this._subscriptions.push(
			this.productsService
				.getMasterProductsTable(this.product.id, params)
				.pipe(finalize(() => this.showLoader = false))
				.subscribe({
					next: (data: ITableData<IMasterProductsTableItem>) => {
						this.product.initMasterProducts(data);
						this.isDirty.set(ProductCardTab.MasterProducts, false);
						this._selectedTablePage = 1;
						this.initSelectedProducts();
					},
					error: () => this.snackBarService.setErrorNotification()
				})
		);
	}

	public onAddProductsSearch(value?: string): void {
		const searchValue = !Utils.isNullOrUndefined(value) ? value : this._requestParams.getParam('search');

		if (!Utils.isNullOrUndefined(searchValue)) {
			this._requestParams.updateParams({
				search: searchValue
			});
		}

		const allFieldsUnselected: boolean = this.searchSettings.addProducts
			.every((setting: ISearchSettings): boolean => !setting.selected);
		const searchFields: Array<string> = this.searchSettings.addProducts
			.filter((setting: ISearchSettings): boolean => allFieldsUnselected || setting.selected)
			.map((setting: ISearchSettings): string => setting.name);

		this._requestParams.updateParams({
			search_fields: searchFields
		});

		this.loadAddProductsData();
	}

	public onApply(): void {
		const data: MPProductsPayload = this.editedTableData.map((item: IMasterProductsTableItem) => ({
			product: item.clickable_params.product_id,
			position: item.position.position
		}));

		this.showLoader = true;
		this.productsService.updateMPProducts(this.product.id, data)
			.subscribe({
				next: (response: MPProductsPayload) => {
					this.loadData();
					this.productCardService.saveProductsSubject.next({
						id: this.product.id,
						products: response
					});
				},
				error: () => {
					this.showLoader = false;
					this.needUpdateAddProducts = true;
					this.snackBarService.setErrorNotification();
				}
			});
	}

	public onCloseSelectedTable(): void {
		this._selectedTablePage = 1;
		if (this.selectedProductsTableComponent) this.selectedProductsTableComponent.scrollToTop();
		if (this.product.masterProducts?.data) this.initSelectedProducts();
	}

	public onDelete(key: number): void {
		this.editedTableData = this.editedTableData.filter((item: IMasterProductsTableItem) => item.key !== key);
		this.isDirty.set(ProductCardTab.MasterProducts, true);
		this.needUpdateAddProducts = true;
		this.product.masterProducts.data.has_data = !!this.editedTableData.length;
		this.initSelectedProducts();
	}

	public onDeleteAll(): void {
		const keys: Array<number> = this.filteredTableData.map((item: IMasterProductsTableItem) => item.key);
		this.editedTableData = this.editedTableData.filter((item: IMasterProductsTableItem) => !keys.includes(item.key));
		this.isDirty.set(ProductCardTab.MasterProducts, true);
		this.needUpdateAddProducts = true;
		this.product.masterProducts.data.has_data = !!this.editedTableData.length;
		this.initSelectedProducts();
	}

	public onSearch(value: string): void {
		this.searchQuery = value;
		this._selectedTablePage = 1;
		this.initSelectedProducts();
	}

	public openAddProducts(): void {
		this.addExpansionPanel?.open();
	}

	public openAddProductsPanel(): void {
		if (this.addTableData) {
			if (this.needUpdateAddProducts) {
				this._requestParams.updateParams({ search: '' });
				this.loadAddProductsData();
			}
			return;
		}
		this.showAddLoader = true;
		this._requestParams = new RequestParams({
			page_size: 30,
			shops: this.storeId === null ? -1 : this.storeId ?? ''
		});
		this.loadAddProductsData();
	}

	public async openFiltersPanel(): Promise<void> {
		this.filtersPanelService.setShowPanel(true);
		if (this._filters.length) {
			setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
			this.filtersPanelService.setFilters(this._filters);
			return;
		}

		const originalCategories: Array<FilterItem> = [];
		const originalBrands: Array<FilterItem> = [];
		const originalMarkers: Array<FilterItem> = [];

		if (this.sessionService.hasFeature(ServiceFeatures.Categories)) {
			const savedParam: any = this._requestParams.getParams(['categories']);
			const categoriesParams: CategoriesParams = {};

			if (savedParam.categories) categoriesParams.ids = savedParam.categories;

			originalCategories.push(...await lastValueFrom(this.filtersService.getCategories(categoriesParams)));
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Brands)) {
			originalBrands.push(...await lastValueFrom(this.filtersService.getBrands()));
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Markers)) {
			const savedParam: any = this._requestParams.getParams(['markers']);
			const markersParams: MarkersParams = {};

			if (savedParam.markers) markersParams.ids = savedParam.markers;

			originalMarkers.push(...await lastValueFrom(this.filtersService.getMarkers(markersParams)));
		}

		const categoriesFilter: Filter = this.filtersService.getCategoriesFilter(
				1,
				originalCategories,
				originalBrands,
				null,
				originalMarkers
			),
			brandsFilter: Filter = this.filtersService.getBrandsFilter(
				2,
				originalBrands,
				null,
				originalMarkers
			),
			suppliersFilter: Filter = this.filtersService.getSuppliersFilter(
				3,
				null,
				originalMarkers
			);

		if (originalMarkers.length) {
			this._filters.push(this.filtersService.getMarkersFilter(
				3,
				originalMarkers,
				[categoriesFilter, brandsFilter, suppliersFilter]
			));
		}

		if (originalCategories.length) {
			this._filters.push(categoriesFilter);
		}

		if (originalBrands.length) {
			this._filters.push(brandsFilter);
		}

		setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
		this.filtersPanelService.setFilters(this._filters);
		this._subscriptions.push(
			this._filtersApplySubscription = this.filtersPanelService.getApplyPanel().subscribe((apply: boolean) => {
				this.filtersApplied = false;

				if (apply) {
					this._filters.forEach((filter: Filter) => {
						const updateParam: Object = {};
						if (
							filter.id === FilterID.ProductsHideOptions &&
							filter.selectingStrategy.getSelected().includes(-1)
						) {
							updateParam[filter.param] = filter.data[0].children.map((f: FilterItem) => f.id);
						} else {
							updateParam[filter.param] = filter.selectingStrategy.getSelected();
						}
						this._requestParams.updateParams(updateParam);
						if (filter.selectingStrategy.isSelected()) this.filtersApplied = true;
					});
				} else {
					this._filters.forEach((filter: Filter) => {
						const updateParam: Object = {};
						updateParam[filter.param] = null;
						this._requestParams.updateParams(updateParam);
					});
				}

				this.loadAddProductsData();
			})
		);
		this._subscriptions.push(
			this.filtersPanelService.getShowPanel().subscribe((show: boolean) => {
				if (!show) setDefaults(this._filters, (param: string) => this._requestParams.getParam(param));
			})
		);
	}

	getSelectedProductsActive(): boolean {
		return this.searchSettings.selectedProducts.some((setting: ISearchSettings): boolean => setting.selected);
	}

	getAddProductsActive(): boolean {
		return this.searchSettings.addProducts.some((setting: ISearchSettings): boolean => setting.selected);
	}

    public uploadProducts(): void {
        const dialogRef = this.dialog.open(ProductsAddExcelModalComponent, {
			width: '600px',
			height: 'fit-content',
			panelClass: 'upload-excel-modal',
			autoFocus: false,
			backdropClass: 'back-drop',
            data: { params: { id_type: 'product', is_master_position: false } }
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (!result || (!result.products && !result.file)) return;

            this.showAddAllProductsLoader = true;
			const productsIds: Array<number> = [];

			if (result.file) {
				const response = await this.addProductsService.uploadExcel(result.file, { is_master_position: false, id_type: 'product' });
                productsIds.push(...response?.results?.products);
			}

			if (result.products) productsIds.push(...result.products);

			if (!productsIds.length) {
                this.showAddAllProductsLoader = false;
				this.snackBarService.setSnackBarNotification({
					messageText: 'PLANOGRAM_EDIT.PRODUCT_LIST_UPLOAD_ERROR',
					durationSeconds: 10
				});
				return;
			}

            if (this.editedTableData.length >= this.sessionService.defaults.max_products_in_mp) {
                this.snackBarService.setSnackBarNotification({
                    messageText: 'MASTER_POSITION.ADD_TOO_MANY_PRODUCTS'
                });
                this.showAddAllProductsLoader = false;
                return;
            }

			const tableIds: Array<number> = this.editedTableData
				.map((item: IMasterProductsTableItem) => item.clickable_params.product_id);

			if (productsIds.every((id: number) => tableIds.includes(id))) {
				this.showAddAllProductsLoader = false;
				return;
			}

            this.productsService
	            .getProductsTable({ ids: productsIds.join(','), exclude_ids: tableIds.join(',') })
	            .subscribe({
	                next: (response: ITableData<IMasterProductsTableItem>) => {
	                    if (!response.count) {
	                        this.snackBarService.setSnackBarNotification({
	                            messageText: 'PLANOGRAM_EDIT.PRODUCT_LIST_UPLOAD_ERROR',
	                            durationSeconds: 10
	                        });
	                        this.showAddAllProductsLoader = false;
	                        return;
	                    }

	                    if (response.count + this.editedTableData.length > this.sessionService.defaults.max_products_in_mp) {
	                        this.snackBarService.setSnackBarNotification({
	                            messageText: 'MASTER_POSITION.ADD_TOO_MANY_PRODUCTS'
	                        });
	                        this.showAddAllProductsLoader = false;
	                        return;
	                    }

	                    const data: Array<IMasterProductsTableItem> = response.results.dataSource
	                        .map((item: IMasterProductsTableItem) => ({
	                            ...item,
	                            position: { dtype: 'mp_product_position', position: null }
	                        }));
	                    this.product.masterProducts.data.results.dataSource.unshift(...data);
	                    this.product.masterProducts.data.count += data.length;
	                    this.product.masterProducts.data.has_data = true;
	                    if (this.addTableData) this.loadAddProductsData();
	                    this.sortingSelectedProductsValue = {
	                        direction: '',
	                        active: '',
	                        value: ''
	                    }
	                    this.initSelectedProducts();
	                    this.isDirty.set(ProductCardTab.MasterProducts, true);
	                    this.showAddAllProductsLoader = false;
	                },
	                error: () => {
	                    this.showAddAllProductsLoader = false;
	                    this.snackBarService.setSnackBarNotification({
	                        messageText: 'PLANOGRAM_EDIT.PRODUCT_LIST_UPLOAD_ERROR',
	                        durationSeconds: 10
	                    });
	                }
            });
		});
    }

	ngOnDestroy(): void {
		this.productCardService.isDirty.set(ProductCardTab.MasterProducts, false);
	}
}
